// eDOS · Search By Specialty. Specialty list comes from the eDOS Laravel
// API (`/get-edos/specialties` returns the distinct, deduped list). The
// list renders as a flat alphabetised grid — the static SPECIALTY_GROUPS
// curation that previously sat in edosData.js was dropped because the
// API's specialty names ("Cardiologist", "Endocrinologist") didn't
// match the curated category names ("Cardiology", "Endocrinology"),
// which would have muted every tile. The flat grid is also what the
// public dos.mytrustlab.com homepage uses, so the two surfaces stay
// consistent.
//
// Selecting a tile fetches the filtered, paginated test list from
// `/get-edos/lab/tests?speciality=<name>`. The active tile shows its
// live total once the result page loads.
import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Close';
import { MP, MONO, DISPLAY } from '../medical/shared';
import { useGetSpecialtiesQuery, useGetTestsListQuery } from '../../../store/edosApi';
import { mapApiToTestRow } from '../../../utils/edosLabAdapter';
import EdosResultsList from './EdosResultsList';
import EdosTestDetail from './EdosTestDetail';

const PER_PAGE = 10;

// Filter out non-clinical noise the eDOS catalogue carries (placeholder
// rows imported from the Excel sheet) so the tile grid is clean.
const SKIP_SPECS = new Set(['NA', 'As per individual tests', 'All Specialties', '']);

const SpecTile = ({ name, count, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      bgcolor: active ? MP.tealDark : MP.white,
      border: `1.5px solid ${active ? MP.tealDark : MP.border}`,
      borderRadius: '6px',
      px: 1.75,
      py: 1.5,
      cursor: 'pointer',
      transition: 'all 0.16s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      '&:hover': {
        borderColor: MP.teal,
        boxShadow: active ? '0 4px 14px rgba(0,153,130,0.25)' : '0 4px 14px rgba(16,21,28,.10)',
        transform: 'translateY(-1px)',
      },
    }}
  >
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 600,
        color: active ? MP.white : MP.dark,
        lineHeight: 1.25,
        letterSpacing: '0.1px',
      }}
    >
      {name}
    </Typography>
    {count != null && (
      <Box
        sx={{
          fontFamily: MONO,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: active ? MP.teal : MP.tealDark,
          bgcolor: active ? 'rgba(255,255,255,0.08)' : MP.tealLight,
          border: `1px solid ${active ? 'rgba(255,255,255,0.15)' : MP.tealMid}`,
          borderRadius: '20px',
          px: 0.875,
          py: 0.125,
          minWidth: 22,
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

const EdosSpeciality = () => {
  const [active, setActive] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedTest, setSelectedTest] = useState(null);
  // Tile-name search box. Client-side filter against the API list —
  // 95+ specialties is a lot to scroll, so the filter helps narrow
  // down quickly.
  const [tileQ, setTileQ] = useState('');
  // In-results test search — server-side keyword filter against the
  // SELECTED specialty (or the whole catalogue when none is active).
  // Debounced so each keystroke doesn't fire a request, and uses
  // ?all=1 so every match comes back in one shot, no pagination.
  const [testQ, setTestQ] = useState('');
  const [debouncedTestQ, setDebouncedTestQ] = useState('');
  // Anchor for the auto-scroll when a tile is clicked.
  const resultsRef = useRef(null);

  // Debounce the in-results search — 350 ms after the user stops typing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTestQ(testQ.trim()), 350);
    return () => clearTimeout(t);
  }, [testQ]);

  // Distinct specialties from the API — alphabetised, noise filtered.
  const specsQuery = useGetSpecialtiesQuery();
  const specialties = useMemo(() => {
    const list = specsQuery.data || [];
    return list
      .filter((s) => s && !SKIP_SPECS.has(String(s).trim()))
      .map((s) => String(s).trim())
      .sort((a, b) => a.localeCompare(b));
  }, [specsQuery.data]);

  // Filter tiles by the search box. Case-insensitive substring match
  // against the specialty name. Empty query → all tiles.
  const filteredSpecs = useMemo(() => {
    const q = tileQ.trim().toLowerCase();
    if (!q) return specialties;
    return specialties.filter((s) => s.toLowerCase().includes(q));
  }, [specialties, tileQ]);

  // Test list — filtered by specialty when one is active, AND by
  // keyword when the in-results search has a value. ?all=1 returns
  // every match in one shot when searching.
  const isSearching = debouncedTestQ.length > 0;
  const listQuery = useGetTestsListQuery({
    page,
    perPage: PER_PAGE,
    ...(active ? { speciality: active } : {}),
    ...(isSearching ? { keyword: debouncedTestQ, all: true } : {}),
  });
  const tests = useMemo(
    () => (listQuery.data?.items || []).map(mapApiToTestRow).filter(Boolean),
    [listQuery.data],
  );
  // Suppress pagination footer when searching.
  const pagination = isSearching ? null : (listQuery.data?.pagination || null);

  const onTileClick = (s) => {
    setActive(active === s ? null : s);
    setPage(1);
    // Smooth-scroll to the results table so the partner sees what
    // changed without having to scroll manually.
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  if (selectedTest) {
    return (
      <EdosTestDetail
        test={selectedTest}
        onBack={() => setSelectedTest(null)}
        onSelectTest={setSelectedTest}
        sectionLabel="Search By Specialty"
      />
    );
  }

  return (
    <Stack spacing={2.5}>
      {/* Picker hero */}
      <Box
        sx={{
          bgcolor: MP.white,
          border: `1px solid ${MP.border}`,
          borderRadius: '6px',
          boxShadow: MP.shadow,
          p: { xs: 2.5, md: 3.5 },
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'flex-end' }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: MP.tealDark,
                mb: 1,
              }}
            >
              eDOS · Search By Specialty
            </Typography>
            <Typography
              sx={{
                fontFamily: DISPLAY,
                fontSize: { xs: 22, md: 28 },
                fontWeight: 700,
                color: MP.dark,
                letterSpacing: '0.5px',
                mb: 0.75,
              }}
            >
              {active ? active : 'Browse by ordering specialty'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: MP.textMuted, maxWidth: '62ch' }}>
              {active
                ? `Tests commonly ordered by ${active} consultants — clinical workup defaults plus the diagnostics inventory we run for this specialty.`
                : `${specialties.length} specialties from the live eDOS catalogue. Pick one to see its recommended panel with TAT, schedule, sample cutoff, and L2L pricing.`}
            </Typography>
          </Box>

          {active && (
            <Box
              onClick={() => { setActive(null); setPage(1); }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                fontFamily: MONO,
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: MP.tealDark,
                bgcolor: MP.tealLight,
                border: `1px solid ${MP.tealMid}`,
                borderRadius: '20px',
                px: 1.5,
                py: 0.75,
                cursor: 'pointer',
                transition: 'all 0.14s',
                alignSelf: { xs: 'flex-start', md: 'center' },
                '&:hover': { bgcolor: MP.teal, color: MP.white, borderColor: MP.teal },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 14 }} /> All Specialties
            </Box>
          )}
        </Stack>

        {/* Tile-name search box — client-side filter. */}
        <Box
          sx={{
            mt: 2.5,
            display: 'flex',
            alignItems: 'center',
            border: `1.5px solid ${tileQ ? MP.teal : MP.border}`,
            borderRadius: '6px',
            bgcolor: MP.bg,
            transition: 'border-color 0.18s, background 0.18s',
            '&:focus-within': { borderColor: MP.teal, bgcolor: MP.white },
          }}
        >
          <Box sx={{ pl: 1.75, display: 'flex', alignItems: 'center', color: MP.textMuted }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
          <InputBase
            value={tileQ}
            onChange={(e) => setTileQ(e.target.value)}
            placeholder="Filter specialties — e.g. Cardiologist, Endocrinologist, Oncologist…"
            sx={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              color: MP.dark,
              px: 1.25,
              py: 1.25,
              fontFamily: DISPLAY,
            }}
          />
          {tileQ && (
            <IconButton size="small" onClick={() => setTileQ('')} sx={{ mr: 0.75 }}>
              <ClearIcon sx={{ fontSize: 16, color: MP.textMuted }} />
            </IconButton>
          )}
        </Box>

        {/* Flat alphabetised tile grid */}
        {specsQuery.isLoading ? (
          <Box
            sx={{
              mt: 2.5,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 1.25,
            }}
          >
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Box
                key={i}
                sx={{
                  bgcolor: MP.bg,
                  border: `1.5px solid ${MP.border}`,
                  borderRadius: '6px',
                  height: 48,
                  '@keyframes lp-pulse': { '0%, 100%': { opacity: 0.4 }, '50%': { opacity: 0.8 } },
                  animation: 'lp-pulse 1.4s ease-in-out infinite',
                }}
              />
            ))}
          </Box>
        ) : filteredSpecs.length === 0 ? (
          // No tile matches the search — explicit message so the
          // partner doesn't think the catalogue is broken.
          <Box
            sx={{
              mt: 2.5,
              p: 3,
              textAlign: 'center',
              bgcolor: MP.bg,
              border: `1px dashed ${MP.border}`,
              borderRadius: '6px',
            }}
          >
            <Typography sx={{ fontFamily: DISPLAY, fontSize: 15, fontWeight: 700, color: MP.dark, mb: 0.5 }}>
              "{tileQ}" is not a specialty in our directory
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: MP.textMuted }}>
              Try a shorter keyword, or use Search By Test Name from the sidebar to look up a specific test.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              mt: 2.5,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 1.25,
            }}
          >
            {filteredSpecs.map((s) => (
              <SpecTile
                key={s}
                name={s}
                count={active === s ? pagination?.total : null}
                active={active === s}
                onClick={() => onTileClick(s)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Results scroll target — onTileClick scrolls here. */}
      <Box ref={resultsRef} sx={{ scrollMarginTop: 80 }}>
        {/* In-results test search — server-side keyword filter scoped
            to the active specialty. ?all=1 returns every match (no
            pagination) so the partner sees the full hit list. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: `1.5px solid ${testQ ? MP.teal : MP.border}`,
            borderRadius: '6px',
            bgcolor: testQ ? MP.white : MP.bg,
            mb: 1.5,
            transition: 'border-color 0.18s, background 0.18s',
            '&:focus-within': { borderColor: MP.teal, bgcolor: MP.white },
          }}
        >
          <Box sx={{ pl: 1.75, display: 'flex', alignItems: 'center', color: MP.textMuted }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
          <InputBase
            value={testQ}
            onChange={(e) => setTestQ(e.target.value)}
            placeholder={
              active
                ? `Search tests for ${active} — name, code, or parameter…`
                : 'Search tests across the catalogue — name, code, or parameter…'
            }
            sx={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              color: MP.dark,
              px: 1.25,
              py: 1.25,
              fontFamily: DISPLAY,
            }}
          />
          {testQ && (
            <IconButton size="small" onClick={() => setTestQ('')} sx={{ mr: 0.75 }}>
              <ClearIcon sx={{ fontSize: 16, color: MP.textMuted }} />
            </IconButton>
          )}
        </Box>

        <EdosResultsList
          tests={tests}
          loading={listQuery.isLoading || listQuery.isFetching}
          error={listQuery.isError ? listQuery.error : null}
          pagination={pagination}
          onPageChange={setPage}
          onSelectTest={setSelectedTest}
          emptyTitle={
            isSearching
              ? (active
                  ? `"${debouncedTestQ}" — not matched in ${active}`
                  : `"${debouncedTestQ}" — no tests match`)
              : (active ? `No tests mapped to ${active} yet` : 'No tests in directory')
          }
          emptySub={
            isSearching
              ? (active
                  ? `No tests for ${active} contain "${debouncedTestQ}". Clear the search to see all ${active} tests, or pick a different specialty.`
                  : 'Try a shorter keyword, or pick a department / specialty to narrow the search.')
              : 'Specialty maps grow with each release — wiring this into the test_master tagging system lands in v2.2.'
          }
        />
      </Box>
    </Stack>
  );
};

export default EdosSpeciality;
